/**
 * Theme Registry
 *
 * Single source of truth for theme metadata (dark/light, label, toggle pair).
 * Every theme listed here must have a matching directory in /public/themes/<name>/
 * (self-hosted CSS — never a public CDN).
 *
 * Per-deploy availability comes from `settings.themes` (root key, overridable via
 * runtime-config.json on the server, no rebuild — same mechanism as
 * bookByPriceRowsPerSide). This registry lists every theme shipped in the build;
 * `getAvailableThemes()` intersects it with the deploy's list.
 *
 * NAMING CONVENTION (mandatory): a theme with `isDark: true` MUST contain "dark"
 * in its name — the inline boot script in index.html applies the dark body class
 * via indexOf("dark") before any module loads, so it cannot read this registry.
 * Enforced by themeRegistry.test.js.
 *
 * @module themes
 */
import { settings } from "../data/Services/settings";

export const THEME_LIGHT = "lara-light-indigo";
export const THEME_DARK = "lara-dark-indigo";

export const THEMES = {
  "lara-light-indigo": {
    // name kept for backward compat (persisted key, CSS dir); UI label is just
    // "Light" — the original two themes predate the odin pair and stay unbranded.
    name: "lara-light-indigo",
    label: "Light",
    isDark: false,
    pair: "lara-dark-indigo",
  },
  "lara-dark-indigo": {
    name: "lara-dark-indigo",
    label: "Dark",
    isDark: true,
    pair: "lara-light-indigo",
  },
  "odin-light": {
    name: "odin-light",
    label: "Odin Light",
    isDark: false,
    pair: "odin-dark",
  },
  "odin-dark": {
    // "Extra Dark" — navy-black deploy-specific theme (Samsung force-dark
    // research). Internal name kept `odin-dark` for the CSS dir / persisted key.
    name: "odin-dark",
    label: "Extra Dark",
    isDark: true,
    pair: "odin-light",
  },
};

/**
 * Whether a theme renders as dark. Unknown names are treated as light — same
 * behavior the old `theme === "lara-dark-indigo"` comparisons had.
 * @param {string} name theme name
 * @returns {boolean}
 */
export const isDarkTheme = (name) => THEMES[name]?.isDark === true;

/**
 * Whether a theme is the "Extra Dark" navy variant. Book/DoM panels use this to
 * add a `--extra-dark` CSS modifier (navy surface deltas layered on `--dark`) and
 * to pick navy inline backgrounds. Centralized so the `odin-dark` name isn't a
 * magic string scattered across components (see docs/research/themes/samsung-internet).
 * @param {string} name theme name
 * @returns {boolean}
 */
export const isExtraDark = (name) => name === "odin-dark";

const warnedThemeIssues = new Set();
const warnOnce = (key, message) => {
  if (warnedThemeIssues.has(key)) return;
  warnedThemeIssues.add(key);
  console.warn(message);
};

/**
 * Themes available on this deploy, resolved at USE time (main.jsx only renders
 * the App after runtime-config.json is applied, so `settings.themes` is final).
 * Operator mistakes in runtime-config never break the app but are never silent
 * either (one-time warns): unknown names are filtered out; an empty/invalid
 * list falls back to the lara pair; a list missing a polarity (no dark or no
 * light theme) is flagged — the toggle can't switch polarity there.
 * @returns {Array<{name: string, label: string, isDark: boolean, pair: string}>}
 */
export const getAvailableThemes = () => {
  const raw = settings.themes;
  const list = Array.isArray(raw) ? raw : [];
  const known = list.filter((name) => {
    if (THEMES[name]) return true;
    warnOnce(
      `unknown:${name}`,
      `[Themes] Unknown theme in settings.themes ignored: ${name}`,
    );
    return false;
  });
  if (known.length === 0) {
    warnOnce(
      "empty-list",
      `[Themes] settings.themes is empty or invalid (${JSON.stringify(raw)}) — falling back to the lara pair`,
    );
    return [THEMES[THEME_LIGHT], THEMES[THEME_DARK]];
  }
  const hasDark = known.some((name) => THEMES[name].isDark);
  const hasLight = known.some((name) => !THEMES[name].isDark);
  if (!hasDark || !hasLight) {
    warnOnce(
      "single-polarity",
      `[Themes] settings.themes has no ${hasDark ? "light" : "dark"} theme — the dark/light toggle can't switch polarity on this deploy`,
    );
  }
  return known.map((name) => THEMES[name]);
};

/**
 * Whether a theme is in this deploy's available list.
 * @param {string} name theme name
 * @returns {boolean}
 */
export const isThemeAvailable = (name) =>
  getAvailableThemes().some((t) => t.name === name);

/**
 * Where the dark/light toggle should land from `current`: the registry `pair`
 * when this deploy offers it, otherwise the first available theme of opposite
 * isDark, otherwise stays put (single-theme deploy).
 * @param {string} current current theme name
 * @returns {string} target theme name
 */
export const resolveToggleTarget = (current) => {
  const available = getAvailableThemes();
  const pairName = THEMES[current]?.pair;
  if (pairName && available.some((t) => t.name === pairName)) return pairName;
  const opposite = available.find((t) => t.isDark !== isDarkTheme(current));
  return opposite ? opposite.name : current;
};

/**
 * Whether the ConfigPanel needs the explicit theme dropdown: true when the
 * dark/light toggle alone can't reach every available theme — more than one
 * pair, or a 2-theme list of the same polarity (toggle dead between them).
 * The default lara pair stays dropdown-free (today's UI).
 * @returns {boolean}
 */
export const themeSelectorNeeded = () => {
  const available = getAvailableThemes();
  if (available.length > 2) return true;
  return available.length === 2 && available[0].isDark === available[1].isDark;
};
